import { Member } from "@/company/members/types";
import { Button } from "@/core/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { ChevronDown, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "../../ui/checkbox";
import { useMemberInput } from "./hooks/use-member-input";

interface MemberInputProps extends React.ComponentPropsWithoutRef<
  typeof Popover
> {
  inputId: string;
  defaultValue: Member[] | null;
  hasEmptyValue: boolean;
  placeholder?: string;
  onChange: (value: Member[] | null) => void;
  /** Called once when members are loaded with total count (e.g. for analytics). */
  onMembersLoaded?: (total: number) => void;
}

export function MemberInput({
  inputId,
  defaultValue,
  hasEmptyValue,
  placeholder,
  onChange,
  onMembersLoaded,
  ...props
}: MemberInputProps) {
  const [translate] = useTranslation(["core"]);
  const { data, total, isLoading, searchParams, setSearchParams } =
    useMemberInput();
  const onMembersLoadedSent = useRef(false);
  const [selectedItem, setSelectedItem] = useState<Member[] | null>(
    defaultValue || null,
  );
  const [scrollPosition, setScrollPosition] = useState(0);
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);

  const handleSearch = useCallback(
    async (value: string) => {
      setSearchParams({ ...searchParams, search: value, page: 1 });
    },
    [setSearchParams, searchParams],
  );

  const handleScroll = useCallback(
    async (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const scrollHeight = target.scrollHeight;
      const scrollTop = target.scrollTop;
      const clientHeight = target.clientHeight;
      const diff = Math.abs(scrollHeight - scrollTop - clientHeight);

      if (diff < 5 && !searchParams.search && !isLoading) {
        setScrollPosition(scrollTop);
        setSearchParams({
          ...searchParams,
          page: (searchParams.page || 1) + 1,
        });
      }
    },
    [setSearchParams, searchParams, isLoading],
  );

  useEffect(() => {
    setFilteredMembers((prev) =>
      searchParams.page === 1 ? data : [...prev, ...data],
    );
  }, [data]);

  useEffect(() => {
    if (scrollPosition > 0) {
      const memberSearch = document.getElementById(`${inputId}-command`);
      const list = memberSearch!.querySelector("[cmdk-list]");

      if (list) {
        list.scrollTop = scrollPosition;
      }
    }
  }, [filteredMembers.length, scrollPosition]);

  const handleSelect = useCallback(
    (member: Member | null) => {
      if (member === null) {
        setSelectedItem(null);
        onChange(null);
      } else {
        setSelectedItem((prev) => {
          if (!prev) {
            const newSelection = [member];
            onChange(newSelection);
            return newSelection;
          }

          const isAlreadySelected = prev.some(
            (selectedMember) => selectedMember.user.id === member.user.id,
          );

          if (isAlreadySelected) {
            const newSelection = prev.filter(
              (selectedMember) => selectedMember.user.id !== member.user.id,
            );
            onChange(newSelection.length > 0 ? newSelection : null);
            return newSelection.length > 0 ? newSelection : null;
          } else {
            const newSelection = [...prev, member];
            onChange(newSelection);
            return newSelection;
          }
        });
      }
      setSearchParams({ ...searchParams, page: 1 });
    },
    [onChange, setSearchParams],
  );

  useEffect(() => {
    setSelectedItem(defaultValue || null);
  }, [defaultValue]);

  useEffect(() => {
    if (
      onMembersLoaded &&
      total != null &&
      total >= 0 &&
      !onMembersLoadedSent.current
    ) {
      onMembersLoadedSent.current = true;
      onMembersLoaded(total);
    }
  }, [onMembersLoaded, total]);

  return (
    <Popover {...props} modal={true}>
      <PopoverTrigger asChild>
        <Button
          id={inputId}
          variant="outline"
          className="justify-between w-full bg-white"
        >
          <span className="overflow-hidden font-normal text-ellipsis">
            {selectedItem && selectedItem.length > 0
              ? selectedItem.length === 1
                ? `${selectedItem[0].user.first_name} ${selectedItem[0].user.last_name}`
                : (translate("members-filter-selected-multiple", {
                    count: selectedItem.length,
                  }) as string)
              : placeholder}
          </span>
          <ChevronDown className="opacity-50 size-4" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="p-0 w-[500px] max-w-[500px]">
        <Command id={`${inputId}-command`}>
          <CommandInput
            placeholder={
              translate("members-filter-search-placeholder") as string
            }
            className="h-9"
            value={searchParams.search}
            onValueChange={handleSearch}
          />
          <CommandList onScrollCapture={handleScroll}>
            <CommandEmpty>
              {translate("members-filter-empty-state") as string}
            </CommandEmpty>
            <CommandGroup>
              {hasEmptyValue && (
                <CommandItem
                  value=""
                  onSelect={() => {
                    handleSelect(null);
                  }}
                >
                  <Checkbox
                    className="mr-2"
                    checked={!selectedItem || selectedItem.length === 0}
                  />

                  {translate("members-filter-placeholder") as string}
                </CommandItem>
              )}
              {filteredMembers.map((member, index) => (
                <CommandItem
                  key={`${member.user.id}-${index}`}
                  value={`${member.user.first_name} ${member.user.last_name} (${member.profile.email})`}
                  onSelect={() => {
                    handleSelect(member);
                  }}
                >
                  <Checkbox
                    className="mr-2"
                    checked={
                      selectedItem?.some(
                        (selectedMember) =>
                          selectedMember.user.id === member.user.id,
                      ) ||
                      selectedItem?.length === 0 ||
                      !selectedItem
                    }
                  />
                  <div className="flex flex-col">
                    {`${member.user.first_name} ${member.user.last_name}`}
                    <span className="text-xs text-gray-500">{`${member.profile.email}`}</span>
                  </div>
                </CommandItem>
              ))}
              {isLoading && <Loader2 className="mx-auto animate-spin size-4" />}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
